import { bool, func, any, node, number, oneOf, oneOfType, string } from 'prop-types';
import React, { useState } from 'react';
import { detect } from 'detect-browser';
import Icon from '../Icon/index';
import css from '@styled-system/css'
import styled from '@emotion/styled';
import CurrencyIcon from './CurrencyIcon/index';
import { toFinite } from 'lodash';

function toCurrency(amount) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: Math.floor(amount) === amount ? 0 : 2,
    maximumFractionDigits: 2,
  });

  return amount === 0 || toFinite(amount) ? formatter.format(amount) : '';
}

const preventDefaultNumberChars = e => {
  let checkIfNum;
  const validKeyCodes = [8, 190]; // backspace and . (dot)
  if (
    e.keyCode &&
    (e.keyCode < 48 || e.keyCode > 57) &&
    !validKeyCodes.some(kc => kc === e.keyCode)
  ) {
    checkIfNum = true;
  } else if (e.key !== undefined) {
    checkIfNum = e.key === 'e' || e.key === '+' || e.key === '-';
  } else if (e.keyCode !== undefined) {
    checkIfNum = e.keyCode === 69 || e.keyCode === 187 || e.keyCode === 189;
  }
  return checkIfNum && e.preventDefault();
};

const browser = detect();

const InputWrapper = styled('div')(
  css({
    display: 'flex',
    borderRadius: "input",
    paddingRight: "10px",
    paddingLeft: "10px",
    outline: "none",
    boxShadow: "none",
  }), ({ width, height, type, theme, error, disabled }) => ({
    width,
    height: type === 'textarea' ? '120px' : height,
    border: `1px solid ${error ? theme.colors.borderError : theme.colors.border}`,
    ":not(:focus-within):hover": {
      border: `1px solid ${theme.colors.borderActive}`
    },
    ":focus-within": {
      outline: "none",
      borderColor: theme.colors.borderFocus,
      boxShadow: `inset 0 0 0 1px ${theme.colors.borderFocus}`
    },
    cursor: disabled ? "not-allowed" : "default",
    backgroundColor: disabled ? theme.colors.backgroundDisabled : "white",
    ":-moz-focusring": { color: "transparent", textShadow: "0 0 0 #000" },
  }));

const TextInput = styled('input')(
  css({
    boxShadow: "none",
    border: "none",
    outline: "none",
    fontSize: "input",
    justifyContent: "center",
    alignItems: "center",
    paddingLeft: "5px",
    background: "transparent",
    resize: 'none',
    width: "100%",
  }), ({ type, theme, disabled, error }) => ({
    cursor: disabled ? "not-allowed" : "initial",
    paddingTop: type === 'textarea' ? '8px' : '0px',
    paddingBottom: type === 'textarea' ? '8px' : '0px',
    "::placeholder": {
      color: theme.colors.placeholder,
      opacity: "1",
      textIndent: "0px"
    },
    color: error ? theme.colors.error : theme.colors.foreground,
  }));

const EyeIcon = styled('div')({
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  width: "20px",
}, ({ isPasswordField }) => ({
  display: isPasswordField ? "inline-flex" : "none",
  cursor: isPasswordField ? "pointer" : "inherit",
  right: isPasswordField ? 0 : 'unset',
}));

const TextIcon = styled('div')({
  justifyContent: "center",
  alignItems: "center",
  height: "100%",
  width: "20px",
}, ({ isIconField }) => ({
  display: !isIconField ? "none" : "inline-flex",
}));

const Input = ({
  as,
  currencyCode,
  className,
  value,
  icon,
  iconColor = "Dark Neutral 1",
  onChange,
  placeholder,
  onBlur,
  error,
  onKeyDown,
  width = "300px",
  height = "40px",
  type = "input",
  min = "0.00",
  step = "0.01",
  disabled = false,
  readOnly = false,
  ...rest
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isObscure, setIsObscure] = useState(true);

  function handleBlur(e) {
    setIsEditing(false);
    if (onBlur) onBlur(e);
  }

  const Comp = as || type === 'textarea' ? 'textarea' : 'input';
  let inputType = type;
  let inputVal = value;
  let isReadOnly = readOnly;

  if (type === "password" && !isObscure) {
    inputType = "text";
  };
  if (currencyCode && isEditing) {
    const isFirefox = browser && browser.name === 'firefox';
    inputType = isFirefox ? 'text' : 'number';
    onKeyDown = preventDefaultNumberChars;
  }
  if (currencyCode && !isEditing) {
    inputVal = toCurrency(value);
    isReadOnly = true;
    inputType = "text";
  }

  return (
    <InputWrapper
      disabled={disabled}
      width={width}
      height={height}
      type={type}
      error={error}
      className={className}
    >
      <TextIcon isIconField={icon || currencyCode}>
        {currencyCode && !icon && <CurrencyIcon currencyCode={currencyCode} />}
        {(icon && typeof icon === 'string') ? <Icon name={icon} color={iconColor} /> : icon}
      </TextIcon>
      <TextInput
        as={Comp}
        type={inputType}
        onFocus={() => setIsEditing(true)}
        readOnly={isReadOnly}
        value={inputVal}
        placeholder={placeholder}
        onKeyDown={onKeyDown}
        onChange={onChange}
        onBlur={handleBlur}
        min={min}
        step={step}
        error={error}
        disabled={disabled}
        {...rest}
      />
      <EyeIcon isPasswordField={type === "password"}>
        <span className="obscure" onClick={() => setIsObscure(!isObscure)}>
          {isObscure ? <Icon name="eye-hidden" /> : <Icon name="eye-visible" />}
        </span>
      </EyeIcon>
    </InputWrapper>
  );
};

Input.propTypes = {
  as: any,
  currencyCode: string,
  className: string,
  value: oneOfType([string, number]),
  icon: node,
  onChange: func,
  placeholder: string,
  onBlur: func,
  error: bool,
  type: oneOf(['text', 'submit', 'email', 'password', 'number', 'tel', 'textarea']),
  width: oneOfType([string, number]),
  height: oneOfType([string, number]),
  min: oneOfType([string, number]),
  step: oneOfType([string, number]),
  readOnly: bool,
  onKeyDown: func,
  unobscure: bool,
};

export default Input;